/**
 * Parse suggested prompts from agent message content
 *
 * Agent can include suggestions in format:
 * [SUGGESTED_PROMPTS]
 * - First suggestion here
 * - Second suggestion here
 * [/SUGGESTED_PROMPTS]
 *
 * Also handles cases where agent forgets closing tag (matches to end of content)
 */

export interface AgentSuggestion {
  prompt: string;
  isAgentGenerated: true;
}

// Primary pattern: Match with proper closing tag
const SUGGESTIONS_PATTERN_CLOSED = /\[SUGGESTED_PROMPTS\]:?\s*([\s\S]*?)\[\/SUGGESTED_PROMPTS\]/;

// Fallback pattern: Match to end of content if no closing tag
// This handles cases where the agent forgets [/SUGGESTED_PROMPTS]
const SUGGESTIONS_PATTERN_OPEN = /\[SUGGESTED_PROMPTS\]:?\s*([\s\S]*)$/;

export function parseAgentSuggestions(content: string): AgentSuggestion[] {
  // Try closed pattern first (with [/SUGGESTED_PROMPTS] closing tag)
  let match = content.match(SUGGESTIONS_PATTERN_CLOSED);
  let patternUsed = 'closed';

  // Fall back to open pattern (no closing tag, matches to end)
  if (!match) {
    match = content.match(SUGGESTIONS_PATTERN_OPEN);
    patternUsed = 'open';
  }

  // Debug logging
  const hasSuggestionsMarker = content.includes('[SUGGESTED_PROMPTS]');
  console.log('[parseAgentSuggestions] Parsing:', {
    contentLength: content.length,
    hasSuggestionsMarker,
    hasClosingTag: content.includes('[/SUGGESTED_PROMPTS]'),
    matchFound: !!match,
    patternUsed: match ? patternUsed : 'none',
    contentTail: content.slice(-200),
  });

  if (!match) {
    return [];
  }

  const suggestionsBlock = match[1];
  const lines = suggestionsBlock.split('\n');

  const suggestions: AgentSuggestion[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Remove bullet points (-, *, •) and numbers (1., 2.)
    const cleaned = trimmed
      .replace(/^[-*•]\s*/, '')
      .replace(/^\d+\.\s*/, '')
      .trim();

    if (cleaned && cleaned.length > 0) {
      suggestions.push({
        prompt: cleaned,
        isAgentGenerated: true,
      });
    }
  }

  const result = suggestions.slice(0, 4); // Max 4 suggestions
  console.log('[parseAgentSuggestions] Extracted suggestions:', result.map(s => s.prompt));
  return result;
}

/**
 * Remove the suggestions block from message content for display
 * Tries closed pattern first, then falls back to open pattern
 */
export function stripSuggestionsFromContent(content: string): string {
  // Try closed pattern first
  let result = content.replace(SUGGESTIONS_PATTERN_CLOSED, '');
  let strippedWith = 'closed';

  // If nothing was replaced, try open pattern
  if (result === content) {
    result = content.replace(SUGGESTIONS_PATTERN_OPEN, '');
    strippedWith = 'open';
  }

  const wasStripped = result !== content;
  console.log('[stripSuggestionsFromContent]', {
    originalLength: content.length,
    strippedLength: result.trim().length,
    wasStripped,
    strippedWith: wasStripped ? strippedWith : 'none',
    hasSuggestionsMarker: content.includes('[SUGGESTED_PROMPTS]'),
  });

  return result.trim();
}

/**
 * Check if content has agent suggestions
 */
export function hasAgentSuggestions(content: string): boolean {
  return SUGGESTIONS_PATTERN_CLOSED.test(content) || SUGGESTIONS_PATTERN_OPEN.test(content);
}
